import React, { useState, useEffect } from 'react';
import { createRecipe, generateRecipeWithAI, generateRecipeWithText } from '../services/api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { PageHeading, Heading, Text, Modal } from '../components/ui';
import { useAuth } from '../context/authContext';
import TimerIcon from '../components/ui/TimerIcon';
import { checkCanCreateRecipe } from '../services/subscriptionService';
import UpgradePrompt from '../components/UpgradePrompt';
import FloatingCreateButton from '../components/FloatingCreateButton';
import RecipeComponentEditor, { AssemblyEditor } from '../components/RecipeComponentEditor';
import Toast from '../components/Toast';
import { logEvent, logError, logDebug } from '../utils/logger';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    notecardBlue: '#4A90E2', // Blue color for notecard dividing lines
    red: '#DC2626', // Red color for title separator
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// TimerEditModal component - Now handles active/inactive timers
function TimerEditModal({ open, onClose, onSave, onRemove, stepIndex, timingSegments = [] }) {
    // Parse timing segments if they're a string
    let segments = timingSegments;
    if (typeof timingSegments === 'string') {
        try {
            segments = JSON.parse(timingSegments);
        } catch (e) {
            segments = [];
        }
    }
    
    // Ensure segments is always an array
    if (!Array.isArray(segments)) {
        segments = [];
    }

    // Ensure we have both active and inactive
    const activeSegment = segments.find(s => s.kind === 'active') || {
        kind: 'active',
        label: 'Active time',
        expectedSeconds: 0
    };
    const inactiveSegment = segments.find(s => s.kind === 'inactive') || {
        kind: 'inactive',
        label: 'Inactive time',
        expectedSeconds: 0
    };

    const [activeMinutes, setActiveMinutes] = useState(Math.floor(activeSegment.expectedSeconds / 60));
    const [activeSeconds, setActiveSeconds] = useState(activeSegment.expectedSeconds % 60);
    const [activeLabel, setActiveLabel] = useState(activeSegment.label || 'Active time');
    
    const [inactiveMinutes, setInactiveMinutes] = useState(Math.floor(inactiveSegment.expectedSeconds / 60));
    const [inactiveSeconds, setInactiveSeconds] = useState(inactiveSegment.expectedSeconds % 60);
    const [inactiveLabel, setInactiveLabel] = useState(inactiveSegment.label || 'Inactive time');

    useEffect(() => {
        if (open) {
            // Re-parse segments in case timingSegments changed
            let currentSegments = timingSegments;
            if (typeof timingSegments === 'string') {
                try {
                    currentSegments = JSON.parse(timingSegments);
                } catch (e) {
                    currentSegments = [];
                }
            }
            if (!Array.isArray(currentSegments)) {
                currentSegments = [];
            }
            
            const active = currentSegments.find(s => s.kind === 'active') || { expectedSeconds: 0, label: 'Active time' };
            const inactive = currentSegments.find(s => s.kind === 'inactive') || { expectedSeconds: 0, label: 'Inactive time' };
            
            setActiveMinutes(Math.floor(active.expectedSeconds / 60));
            setActiveSeconds(active.expectedSeconds % 60);
            setActiveLabel(active.label || 'Active time');
            
            setInactiveMinutes(Math.floor(inactive.expectedSeconds / 60));
            setInactiveSeconds(inactive.expectedSeconds % 60);
            setInactiveLabel(inactive.label || 'Inactive time');
        }
    }, [open, timingSegments]);

    const handleSave = () => {
        const activeTotalSeconds = activeMinutes * 60 + activeSeconds;
        const inactiveTotalSeconds = inactiveMinutes * 60 + inactiveSeconds;
        
        const newSegments = [
            {
                kind: 'active',
                label: activeLabel,
                expectedSeconds: activeTotalSeconds,
                order: 0
            },
            {
                kind: 'inactive',
                label: inactiveLabel,
                expectedSeconds: inactiveTotalSeconds,
                order: 1
            }
        ];
        
        onSave(newSegments);
    };

    if (!open) return null;
    
    const totalMinutes = activeMinutes + inactiveMinutes;
    const totalSeconds = activeSeconds + inactiveSeconds;
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 min-w-[400px] max-w-[90vw]">
                <h2 className="font-mono text-lg mb-4">Edit Timers for Step {stepIndex + 1}</h2>
                
                {/* Active Timer (Red) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#fee2e2', border: '1px solid #fca5a5' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#991b1b' }}>ACTIVE TIME</label>
                    <input
                        type="text"
                        value={activeLabel}
                        onChange={e => setActiveLabel(e.target.value)}
                        placeholder="e.g., Whisking to stiff peaks"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={activeMinutes}
                                onChange={e => setActiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={activeSeconds}
                                onChange={e => setActiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Inactive Timer (Blue) */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}>
                    <label className="block font-mono text-xs mb-2 font-bold" style={{ color: '#1e40af' }}>INACTIVE TIME</label>
                    <input
                        type="text"
                        value={inactiveLabel}
                        onChange={e => setInactiveLabel(e.target.value)}
                        placeholder="e.g., Baking until golden brown"
                        className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm mb-2"
                    />
                    <div className="flex gap-2 items-center">
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Minutes</label>
                            <input
                                type="number"
                                min="0"
                                value={inactiveMinutes}
                                onChange={e => setInactiveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block font-mono text-xs mb-1">Seconds</label>
                            <input
                                type="number"
                                min="0"
                                max="59"
                                value={inactiveSeconds}
                                onChange={e => setInactiveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                className="w-full px-2 py-1 border border-neutral-300 rounded font-mono text-sm"
                            />
                        </div>
                    </div>
                </div>

                {/* Total Duration Display */}
                <div className="mb-4 p-2 bg-neutral-50 rounded text-center">
                    <span className="font-mono text-xs text-neutral-600">Total: {totalMinutes}m {totalSeconds}s</span>
                </div>

                <div className="flex gap-2 justify-end">
                    <button
                        className="px-4 py-2 bg-neutral-200 text-neutral-900 rounded font-mono text-xs hover:bg-neutral-300"
                        onClick={onClose}
                    >Cancel</button>
                    {onRemove && (
                        <button
                            className="px-4 py-2 bg-red-600 text-white rounded font-mono text-xs hover:bg-red-700"
                            onClick={onRemove}
                        >Remove</button>
                    )}
                    <button
                        className="px-4 py-2 bg-neutral-900 text-white rounded font-mono text-xs hover:bg-neutral-800"
                        onClick={handleSave}
                    >Save</button>
                </div>
            </div>
        </div>
    );
}

const CreateRecipe = () => {
    const location = useLocation();
    const scheduledDate = location.state?.scheduledDate;
    const returnTo = location.state?.returnTo || '/recipes';
    
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        cooking_time: '',
        // Multi-component flag - checked first after cooking_time
        isMultiComponent: false,
        // Simple mode fields (used when isMultiComponent: false)
        ingredients: [''],
        prerequisites: [''],
        steps: [''],
        // Multi-component mode fields (used when isMultiComponent: true)
        components: [],
        assembly: { prerequisites: [''], steps: [''] },
        // Other fields
        images: [],
        timerAssociations: [],
    });
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [dragOver, setDragOver] = useState(false);
    
    // AI Context Generation State
    const [transferMode, setTransferMode] = useState('url'); // 'url' or 'text'
    const [urlContext, setUrlContext] = useState('');
    const [textContent, setTextContent] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiGenerated, setAiGenerated] = useState(false);
    const [highlightFields, setHighlightFields] = useState({});
    const [progressData, setProgressData] = useState({ step: '', message: '', progress: 0 });
    const [sourceUrl, setSourceUrl] = useState('');
    const [showAuthorModal, setShowAuthorModal] = useState(false);
    const [manualAuthor, setManualAuthor] = useState('');
    const [showSmartTransferInfo, setShowSmartTransferInfo] = useState(false);
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
    const [showPasteAttributionModal, setShowPasteAttributionModal] = useState(false);
    const [pasteSourceUrl, setPasteSourceUrl] = useState('');
    const [pasteAuthor, setPasteAuthor] = useState('');
    
    // Pre-transfer acknowledgment state
    const [acknowledgmentAccepted, setAcknowledgmentAccepted] = useState(false);
    const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
    // Check if user has seen acknowledgment before
    const [hasSeenAcknowledgment, setHasSeenAcknowledgment] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('smartTransferAcknowledgmentSeen') === 'true';
        }
        return false;
    });
    
    // Recipe type help popup state - only for transfer section (removed from main form section)
    const [showRecipeTypeHelpTransfer, setShowRecipeTypeHelpTransfer] = useState(false);
    // Track if recipe type has been explicitly set (required for transfer)
    const [recipeTypeExplicitlySet, setRecipeTypeExplicitlySet] = useState(false);
    
    // Subscription quota state
    const [quotaCheck, setQuotaCheck] = useState(null);
    const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
    const [quotaLoading, setQuotaLoading] = useState(true);
    
    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();

    const categories = [
        'Breakfast', 'Brunch', 'Lunch', 'Dinner', 'Snack', 
        'Dessert', 'Supper', 'Appetizer', 'Side', 'Drink', 
        'Condiment', 'Bread', 'Salad', 'Soup'
    ];

    // Check quota on component mount
    useEffect(() => {
        const checkQuota = async () => {
            try {
                const quota = await checkCanCreateRecipe();
                setQuotaCheck(quota);
            } catch (error) {
                console.error('Failed to check quota:', error);
                // Default to allowing creation if check fails
                setQuotaCheck({ canCreate: true, planType: 'free' });
            } finally {
                setQuotaLoading(false);
            }
        };

        if (currentUser) {
            checkQuota();
        } else {
            setQuotaLoading(false);
        }
    }, [currentUser]);

    // Reset acknowledgment when transfer mode changes (but keep hasSeenAcknowledgment)
    useEffect(() => {
        if (hasSeenAcknowledgment) {
            setAcknowledgmentAccepted(false);
        }
    }, [transferMode, hasSeenAcknowledgment]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleListChange = (listName, index, value) => {
        setFormData(prev => ({
            ...prev,
            [listName]: prev[listName].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (listName) => {
        setFormData(prev => ({
            ...prev,
            [listName]: [...prev[listName], '']
        }));
    };

    const removeListItem = (listName, index) => {
        if (formData[listName].length > 1) {
            setFormData(prev => ({
                ...prev,
                [listName]: prev[listName].filter((_, i) => i !== index)
            }));
        }
    };

    // Multi-component handlers
    const toggleMultiComponent = () => {
        setRecipeTypeExplicitlySet(true);
        setFormData(prev => ({
            ...prev,
            isMultiComponent: !prev.isMultiComponent,
            // When switching to multi-component, initialize with one component if empty
            components: !prev.isMultiComponent && prev.components.length === 0 
                ? [{ 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }] 
                : prev.components
        }));
    };

    const addComponent = () => {
        setFormData(prev => ({
            ...prev,
            components: [
                ...prev.components,
                { 
                    id: `component-${Date.now()}`, 
                    name: '', 
                    ingredients: [''], 
                    prerequisites: [''], 
                    steps: [''],
                    subComponents: []
                }
            ]
        }));
    };

    const updateComponent = (index, updatedComponent) => {
        setFormData(prev => ({
            ...prev,
            components: prev.components.map((comp, i) => 
                i === index ? { ...comp, ...updatedComponent } : comp
            )
        }));
    };

    const removeComponent = (index) => {
        if (formData.components.length > 1) {
            setFormData(prev => ({
                ...prev,
                components: prev.components.filter((_, i) => i !== index)
            }));
        }
    };

    const updateAssembly = (newAssembly) => {
        setFormData(prev => ({
            ...prev,
            assembly: newAssembly
        }));
    };

    // Timer Association Handlers
    const addTimerAssociation = () => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: [
                ...prev.timerAssociations,
                { stepIndex: 0, duration: 0, ingredients: [''] }
            ]
        }));
    };
    const updateTimerAssociation = (idx, field, value) => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: prev.timerAssociations.map((ta, i) =>
                i === idx ? { ...ta, [field]: value } : ta
            )
        }));
    };
    const removeTimerAssociation = (idx) => {
        setFormData(prev => ({
            ...prev,
            timerAssociations: prev.timerAssociations.filter((_, i) => i !== idx)
        }));
    };

    const handleImageUpload = (files) => {
        const fileArray = Array.from(files);
        
        // Validate files
        const validFiles = fileArray.filter(file => {
            if (!file.type.startsWith('image/')) {
                console.warn(`File ${file.name} is not an image`);
                return false;
            }
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                console.warn(`File ${file.name} is too large (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            setError('Please select valid image files (PNG, JPG, GIF up to 10MB each)');
            return;
        }

        // Add files to state
        setImageFiles(prev => [...prev, ...validFiles]);
        
        // Create previews
        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreviews(prev => [...prev, e.target.result]);
            };
            reader.onerror = () => {
                console.error(`Error reading file ${file.name}`);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleImageUpload(e.target.files);
            // Clear the input value to allow selecting the same file again
            e.target.value = '';
        }
    };

    const triggerFileInput = () => {
        const input = document.getElementById('card-image-input');
        if (input) {
            input.click();
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleImageUpload(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const removeImage = (index) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Check quota before allowing submission (skip for admin/family users)
        if (quotaCheck && !quotaCheck.canCreate && !quotaCheck.isUnlimited) {
            setShowUpgradePrompt(true);
            return;
        }

        const { name, category, cooking_time, isMultiComponent, ingredients, prerequisites, steps, components, assembly } = formData;

        // Validation
        if (!name.trim()) {
            setError('Recipe name is required.');
            return;
        }
        if (!category) {
            setError('Please select a category.');
            return;
        }
        if (!cooking_time || cooking_time <= 0) {
            setError('Please enter a valid cooking time.');
            return;
        }
        
        // Validation differs based on isMultiComponent flag
        if (isMultiComponent) {
            // Multi-component validation
            if (!components || components.length === 0) {
                setError('Please add at least one component.');
                return;
            }
            
            // Validate each component has ingredients and steps
            for (let i = 0; i < components.length; i++) {
                const comp = components[i];
                if (!comp.name || !comp.name.trim()) {
                    setError(`Component ${i + 1} needs a name.`);
                    return;
                }
                const compIngredients = (comp.ingredients || []).filter(ing => ing && ing.trim());
                if (compIngredients.length === 0) {
                    setError(`Component "${comp.name}" needs at least one ingredient.`);
                    return;
                }
                const compSteps = (comp.steps || []).filter(step => {
                    if (typeof step === 'string') return step.trim();
                    if (step && step.text) return step.text.trim();
                    return false;
                });
                if (compSteps.length === 0) {
                    setError(`Component "${comp.name}" needs at least one step.`);
                    return;
                }
            }
            
            // Validate assembly has at least one step
            const assemblySteps = (assembly?.steps || []).filter(step => {
                if (typeof step === 'string') return step.trim();
                if (step && step.text) return step.text.trim();
                return false;
            });
            if (assemblySteps.length === 0) {
                setError('Please add at least one assembly step to combine the components.');
                return;
            }
        } else {
            // Simple recipe validation
        const validIngredients = ingredients.filter(ing => ing.trim());
        if (validIngredients.length === 0) {
            setError('Please add at least one ingredient.');
            return;
        }
        
        const validSteps = steps.filter(step => step.trim());
        if (validSteps.length === 0) {
            setError('Please add at least one cooking step.');
            return;
            }
        }

        try {
            setIsSubmitting(true);
            if (!currentUser) throw new Error('User not authenticated');
            const token = await currentUser.getIdToken();

            const submitData = new FormData();
            submitData.append('name', name);
            submitData.append('category', category);
            submitData.append('cooking_time', cooking_time);
            submitData.append('isMultiComponent', isMultiComponent);

            if (isMultiComponent) {
                // Multi-component submission - all data in components and assembly
                submitData.append('components', JSON.stringify(components));
                submitData.append('assembly', JSON.stringify(assembly));
                // Empty instructions for multi-component (data is in components)
                submitData.append('instructions', JSON.stringify({ prerequisites: [], steps: [] }));
                submitData.append('ingredients', JSON.stringify([]));
            } else {
                // Simple recipe submission
                const validIngredients = ingredients.filter(ing => ing.trim());
                const validPrerequisites = prerequisites.filter(pre => pre.trim());
                const validSteps = steps.filter(step => step.trim());

            const instructions = {
                prerequisites: validPrerequisites.map(p => p.trim()).filter(Boolean),
                steps: validSteps.map(s => s.trim()).filter(Boolean)
            };

            // Convert stepTimingSegments to a format the backend can use
            const stepsWithTiming = validSteps.map((stepText, index) => {
                const segments = stepTimingSegments[index] || [];
                return {
                    text: stepText,
                    timing_segments: segments.length > 0 ? segments : null
                };
            });

            submitData.append('instructions', JSON.stringify(instructions));
            submitData.append('ingredients', JSON.stringify(validIngredients));
            submitData.append('timer_associations', JSON.stringify(formData.timerAssociations));
            submitData.append('steps_with_timing', JSON.stringify(stepsWithTiming));
            }

            // Add source URL and author if recipe was transferred from AI
            if (sourceUrl && aiGenerated) {
                submitData.append('ai_source_url', sourceUrl);
                submitData.append('source_url', sourceUrl);
            }
            
            // Add author if available
            if (formData.author) {
                submitData.append('author', formData.author);
            }

            imageFiles.forEach((image) => {
                submitData.append('images', image);
            });

            const createdRecipe = await createRecipe(submitData, token);
            logEvent('recipe.created', {
                recipeId: createdRecipe?.id,
                name: name,
                isMultiComponent: isMultiComponent
            });
            
            // If scheduled date is provided, automatically schedule the recipe
            if (scheduledDate && createdRecipe?.id) {
                try {
                    const menuSchedulingService = (await import('../services/menuSchedulingService')).default;
                    await menuSchedulingService.scheduleMeal(
                        createdRecipe.id,
                        scheduledDate,
                        null, // meal_type
                        null, // notes
                        null, // requires_day_before_prep (will be auto-detected)
                        token
                    );
                } catch (scheduleError) {
                    console.error('Error scheduling recipe:', scheduleError);
                    // Don't block navigation if scheduling fails
                }
            }
            
            navigate(returnTo);
        } catch (err) {
            logError('[CreateRecipe]', 'Error creating recipe', err);
            logEvent('recipe.create.failed', {
                name: name,
                error: err.message || 'Unknown error'
            });
            setError('Failed to create recipe. Please try again.');
            setIsSubmitting(false);
        }
    };

    const handleAIGeneration = async () => {
        // Validation based on mode
        if (transferMode === 'url' && !urlContext.trim()) {
            setError('Please enter a valid recipe URL to transfer.');
            return;
        }
        
        if (transferMode === 'text' && !textContent.trim()) {
            setError('Please paste recipe content to transfer.');
            return;
        }

        if (!currentUser) {
            setError('You must be logged in to use AI recipe transfer.');
            return;
        }

        // Check if user needs to see acknowledgment modal (first time)
        if (!hasSeenAcknowledgment) {
            setShowAcknowledgmentModal(true);
            return;
        }

        // Check acknowledgment (for subsequent uses)
        if (!acknowledgmentAccepted) {
            setError('Please confirm the acknowledgment before transferring.');
            return;
        }

        // For paste mode, show attribution modal first
        if (transferMode === 'text') {
            setShowPasteAttributionModal(true);
            return;
        }

        // Continue with URL mode
        await performTransfer();
    };

    const handleAcknowledgmentAccept = () => {
        // Mark as seen in localStorage
        if (typeof window !== 'undefined') {
            localStorage.setItem('smartTransferAcknowledgmentSeen', 'true');
        }
        setHasSeenAcknowledgment(true);
        setAcknowledgmentAccepted(true);
        setShowAcknowledgmentModal(false);
        
        // Continue with transfer process
        if (transferMode === 'text') {
            setShowPasteAttributionModal(true);
        } else {
            performTransfer();
        }
    };

    const performTransfer = async () => {
        try {
            setIsGenerating(true);
            setError('');
            setProgressData({ step: '', message: 'Starting recipe transfer...', progress: 0 });

            const token = await currentUser.getIdToken();
            
            // Prepare user recipe data
            const userRecipeData = {
                name: formData.name || '',
                category: formData.category || '',
                cooking_time: parseInt(formData.cooking_time) || 0,
                ingredients: formData.ingredients.filter(item => item.trim() !== ''),
                prerequisites: formData.prerequisites.filter(item => item.trim() !== ''),
                steps: formData.steps.filter(item => item.trim() !== ''),
                isMultiComponent: formData.isMultiComponent
            };

            let result;
            let extractedSourceUrl = '';

            if (transferMode === 'url') {
                result = await generateRecipeWithAI({
                    userRecipeData,
                    url: urlContext
                }, token, (progress) => {
                    setProgressData(progress);
                });
                extractedSourceUrl = result.sourceUrl || urlContext;
            } else {
                result = await generateRecipeWithText({
                    userRecipeData,
                    textContent: textContent
                }, token, (progress) => {
                    setProgressData(progress);
                });
                // Use the URL and author from attribution modal
                extractedSourceUrl = pasteSourceUrl || '';
            }

            const enhancedRecipe = result.recipe || result;
            
            // For pasted content, override author with manual input if provided
            if (transferMode === 'text' && pasteAuthor) {
                enhancedRecipe.author = pasteAuthor;
            }
            
            // Debug logging

            // Extract steps
            const newSteps = enhancedRecipe.instructions?.steps && enhancedRecipe.instructions.steps.length > 0
                ? enhancedRecipe.instructions.steps
                : (formData.steps[0] ? formData.steps : ['']);

            // Convert timing data to stepTimingSegments format
            const newStepTimingSegments = {};
            
            // First, try to use steps_with_timing if available
            if (enhancedRecipe.steps_with_timing && Array.isArray(enhancedRecipe.steps_with_timing)) {
                enhancedRecipe.steps_with_timing.forEach((stepWithTiming, index) => {
                    if (stepWithTiming.timing_segments && Array.isArray(stepWithTiming.timing_segments)) {
                        // Ensure we have both active and inactive timers
                        const segments = stepWithTiming.timing_segments;
                        const hasActive = segments.some(s => s.kind === 'active');
                        const hasInactive = segments.some(s => s.kind === 'inactive');
                        
                        if (hasActive && hasInactive) {
                            newStepTimingSegments[index] = segments;
                        } else {
                            // Normalize to ensure both exist
                            const active = segments.find(s => s.kind === 'active') || {
                                kind: 'active',
                                label: 'Active time',
                                expectedSeconds: 0,
                                order: 0
                            };
                            const inactive = segments.find(s => s.kind === 'inactive') || {
                                kind: 'inactive',
                                label: 'No waiting required',
                                expectedSeconds: 0,
                                order: 1
                            };
                            newStepTimingSegments[index] = [active, inactive];
                        }
                    }
                });
            }
            
            // If no steps_with_timing, convert timerAssociations to timing_segments with intelligent splitting
            if (Object.keys(newStepTimingSegments).length === 0 && enhancedRecipe.timerAssociations && Array.isArray(enhancedRecipe.timerAssociations)) {
                newSteps.forEach((stepText, index) => {
                    const ta = enhancedRecipe.timerAssociations.find(t => t.stepIndex === index);
                    if (ta && ta.duration) {
                        // Convert duration (in minutes) to seconds
                        const durationSeconds = ta.duration * 60;
                        
                        // Intelligently split based on step text
                        const stepLower = (stepText || '').toLowerCase();
                        const hasInactiveKeywords = stepLower.includes('let rest') || stepLower.includes('rest for') ||
                                                   stepLower.includes('let sit') || stepLower.includes('let stand') ||
                                                   stepLower.includes('let rise') || stepLower.includes('let proof') ||
                                                   stepLower.includes('let cool') || stepLower.includes('let chill') ||
                                                   stepLower.includes('let set') || stepLower.includes('let marinate') ||
                                                   stepLower.includes('let soak') || stepLower.includes('bake') ||
                                                   stepLower.includes('roast') || stepLower.includes('broil') ||
                                                   stepLower.includes('simmer') || stepLower.includes('boil');
                        
                        // Try to extract explicit inactive time
                        const restMatch = stepLower.match(/(?:let\s+)?rest\s+for\s+(\d+)\s+min/i) ||
                                         stepLower.match(/(?:let\s+)?rise\s+for\s+(\d+)\s+min/i);
                        
                        let activeSeconds, inactiveSeconds;
                        if (restMatch) {
                            // Extract explicit inactive time
                            inactiveSeconds = parseInt(restMatch[1]) * 60;
                            activeSeconds = Math.max(0, durationSeconds - inactiveSeconds);
                        } else if (hasInactiveKeywords) {
                            // Estimate 20-30% active, 70-80% inactive
                            activeSeconds = Math.round(durationSeconds * 0.25);
                            inactiveSeconds = durationSeconds - activeSeconds;
                        } else {
                            // All active time
                            activeSeconds = durationSeconds;
                            inactiveSeconds = 0;
                        }
                        
                        newStepTimingSegments[index] = [
                            {
                                kind: 'active',
                                label: ta.description || `Step ${index + 1} active time`,
                                expectedSeconds: activeSeconds,
                                order: 0
                            },
                            {
                                kind: 'inactive',
                                label: inactiveSeconds > 0 ? `Waiting for ${Math.round(inactiveSeconds / 60)} minutes` : 'No waiting required',
                                expectedSeconds: inactiveSeconds,
                                order: 1
                            }
                        ];
                    }
                });
            }

            // Check if AI detected multi-component recipe
            const isMultiComponent = enhancedRecipe.isMultiComponent === true;
            
            console.log('🔍 Multi-component detection:', {
                isMultiComponent,
                hasComponents: Boolean(enhancedRecipe.components),
                componentCount: enhancedRecipe.components?.length || 0,
                componentNames: enhancedRecipe.components?.map(c => c.name) || [],
                rawIsMultiComponent: enhancedRecipe.isMultiComponent
            });
            
            let newFormData;
            let fieldsChanged;
            
            if (isMultiComponent && enhancedRecipe.components && enhancedRecipe.components.length > 0) {
                console.log('✅ Processing as MULTI-COMPONENT recipe');
                // Multi-component recipe from AI
                newFormData = {
                ...formData,
                name: enhancedRecipe.name || formData.name,
                category: enhancedRecipe.category || formData.category,
                cooking_time: enhancedRecipe.cooking_time || formData.cooking_time,
                    isMultiComponent: true,
                    // Clear simple mode fields
                    ingredients: [],
                    prerequisites: [],
                    steps: [],
                    // Set multi-component fields
                    components: enhancedRecipe.components.map((comp, idx) => ({
                        id: comp.id || `component-${Date.now()}-${idx}`,
                        name: comp.name || '',
                        ingredients: comp.ingredients || [''],
                        prerequisites: comp.prerequisites || [''],
                        steps: comp.steps || [''],
                        subComponents: comp.subComponents || []
                    })),
                    assembly: enhancedRecipe.assembly || { prerequisites: [''], steps: [''] },
                    author: enhancedRecipe.author || null,
                    source_url: extractedSourceUrl || null
                };
                
                fieldsChanged = {
                    name: enhancedRecipe.name && enhancedRecipe.name !== formData.name,
                    category: enhancedRecipe.category && enhancedRecipe.category !== formData.category,
                    cooking_time: enhancedRecipe.cooking_time && enhancedRecipe.cooking_time !== formData.cooking_time,
                    isMultiComponent: true,
                    components: true
                };
            } else {
                // Simple recipe from AI (original logic)
                console.log('📝 Processing as SIMPLE recipe');
                newFormData = {
                    ...formData,
                    name: enhancedRecipe.name || formData.name,
                    category: enhancedRecipe.category || formData.category,
                    cooking_time: enhancedRecipe.cooking_time || formData.cooking_time,
                    isMultiComponent: false,
                ingredients: enhancedRecipe.ingredients && enhancedRecipe.ingredients.length > 0 
                    ? enhancedRecipe.ingredients 
                    : (formData.ingredients[0] ? formData.ingredients : ['']),
                prerequisites: enhancedRecipe.instructions?.prerequisites && enhancedRecipe.instructions.prerequisites.length > 0
                    ? enhancedRecipe.instructions.prerequisites
                    : (formData.prerequisites[0] ? formData.prerequisites : ['']),
                steps: newSteps,
                timerAssociations: enhancedRecipe.timerAssociations && enhancedRecipe.timerAssociations.length > 0
                    ? enhancedRecipe.timerAssociations
                    : formData.timerAssociations,
                author: enhancedRecipe.author || null,
                source_url: extractedSourceUrl || null
            };

                fieldsChanged = {
                name: enhancedRecipe.name && enhancedRecipe.name !== formData.name,
                category: enhancedRecipe.category && enhancedRecipe.category !== formData.category,
                cooking_time: enhancedRecipe.cooking_time && enhancedRecipe.cooking_time !== formData.cooking_time,
                ingredients: enhancedRecipe.ingredients && JSON.stringify(enhancedRecipe.ingredients) !== JSON.stringify(formData.ingredients),
                prerequisites: enhancedRecipe.instructions?.prerequisites && JSON.stringify(enhancedRecipe.instructions.prerequisites) !== JSON.stringify(formData.prerequisites),
                steps: enhancedRecipe.instructions?.steps && JSON.stringify(enhancedRecipe.instructions.steps) !== JSON.stringify(formData.steps)
            };
            }

            
            // Calculate cooking_time from timing_segments (active + inactive for all steps)
            let calculatedCookingTime = 0;
            if (Object.keys(newStepTimingSegments).length > 0) {
                // Sum all active and inactive seconds from all steps
                let totalSeconds = 0;
                Object.values(newStepTimingSegments).forEach(segments => {
                    if (Array.isArray(segments)) {
                        segments.forEach(segment => {
                            if (segment && typeof segment.expectedSeconds === 'number') {
                                totalSeconds += segment.expectedSeconds;
                            }
                        });
                    }
                });
                // Convert to minutes (round up)
                calculatedCookingTime = Math.ceil(totalSeconds / 60);
            }
            
            // Update cooking_time if we calculated it from timing_segments
            if (calculatedCookingTime > 0) {
                newFormData.cooking_time = calculatedCookingTime;
                console.log(`✅ Calculated cooking_time from timing_segments: ${calculatedCookingTime} minutes`);
            }
            
            // Update state
            setFormData(newFormData);
            setStepTimingSegments(newStepTimingSegments);
            setHighlightFields(fieldsChanged);
            setAiGenerated(true);
            setSourceUrl(extractedSourceUrl);
            
            // Show success toast
            setSuccessMessage('Recipe successfully transferred to your cookbook');
            
            // Reset acknowledgment after successful transfer (user will need to check box again next time)
            if (hasSeenAcknowledgment) {
                setAcknowledgmentAccepted(false);
            }
            setError('');
            
            // Show author modal if no author was detected (but not for paste mode since we already asked)
            if (!enhancedRecipe.author && transferMode !== 'text') {
                setShowAuthorModal(true);
            }

        } catch (err) {
            console.error('❌ Error generating recipe with AI:', err);
            
            // Provide more helpful error messages
            let errorMessage = err.message || 'Failed to transfer recipe. Please try again.';
            
            if (err.message.includes('Invalid URL format')) {
                errorMessage = 'Invalid URL format. Please check the URL and try again.';
            } else if (err.message.includes('Connection was reset')) {
                errorMessage = 'Connection was reset by the server. The website may be temporarily unavailable. Please try again in a few minutes.';
            } else if (err.message.includes('Request timed out')) {
                errorMessage = 'Request timed out. The website is taking too long to respond. Please try again.';
            } else if (err.message.includes('Website not found')) {
                errorMessage = 'Website not found. Please check the URL and try again.';
            } else if (err.message.includes('Access denied')) {
                // Show special modal for access denied error
                setShowAccessDeniedModal(true);
                errorMessage = 'Access denied. The website is blocking automated requests.';
            } else if (err.message.includes('Recipe page not found')) {
                errorMessage = 'Recipe page not found. Please check the URL and make sure it points to a valid recipe.';
            } else if (err.message.includes('Too many requests')) {
                errorMessage = 'Too many requests. Please wait a moment and try again.';
            } else if (err.message.includes('Website server error')) {
                errorMessage = 'Website server error. Please try again later.';
            } else if (err.message.includes('Unable to extract recipe data')) {
                errorMessage = 'Unable to extract recipe data from this website. The site may not contain a recognizable recipe format, or it may be blocking automated access. Try a different recipe URL.';
            }
            
            setError(errorMessage);
        } finally {
            setIsGenerating(false);
            setProgressData({ step: '', message: '', progress: 0 });
        }
    };

    const [timerModal, setTimerModal] = useState({ open: false, timerIndex: null, stepIndex: null, timingSegments: null });
    const [stepTimingSegments, setStepTimingSegments] = useState({}); // Store timing_segments for each step

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Floating Create Button - transforms to back arrow on create page */}
            {currentUser && <FloatingCreateButton />}
            
            <div className="w-full px-0 py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle="BUILD NEW RECIPE FROM SCRATCH"
                >
                    CREATE RECIPE
                </PageHeading>


                {/* Toast Messages */}
                {error && (
                    <Toast
                        message={error}
                        type="error"
                        onClose={() => setError('')}
                        duration={5000}
                    />
                )}
                {successMessage && (
                    <Toast
                        message={successMessage}
                        type="success"
                        onClose={() => setSuccessMessage('')}
                        duration={5000}
                    />
                )}

                {/* Smart Transfer - Same Width as Recipe Card */}
                <div className="w-full flex justify-center px-0 mb-2">
                    <div className="w-full max-w-[95%] sm:max-w-[630px] md:max-w-[1000px]">
                        <div className="bg-neutral-0 hairline minimal-padding">
                            <div className="mb-3">
                                <div className="flex items-center gap-3 mb-2">
                                    <Heading level={2} style={{ color: '#1b263b' }}>Smart Transfer</Heading>
                                    <button
                                        type="button"
                                        onClick={() => setShowSmartTransferInfo(true)}
                                        className="w-6 h-6 bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 hairline flex items-center justify-center text-sm font-mono transition-colors"
                                        title="Learn how Smart Transfer works"
                                    >
                                        i
                                    </button>
                                </div>
                                <div className="w-full h-px bg-neutral-900 opacity-20"></div>
                            </div>
                            
                            <div className="space-y-3">
                                {/* Toggle between URL and Text modes */}
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setTransferMode('url')}
                                        disabled={isGenerating}
                                        className={`px-3 py-1 font-mono text-xs uppercase tracking-wider hairline transition-colors ${
                                            transferMode === 'url' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        URL
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTransferMode('text')}
                                        disabled={isGenerating}
                                        data-walkthrough="paste-text-button"
                                        className={`px-3 py-1 font-mono text-xs uppercase tracking-wider hairline transition-colors ${
                                            transferMode === 'text' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        PASTE TEXT
                                    </button>
                                </div>

                                {/* Input and Recipe Type Row - Same Height */}
                                <div className="flex flex-col sm:flex-row gap-2 items-start">
                                    {/* URL/Text Input */}
                                    <div className="flex-1 w-full sm:w-auto">
                                        {transferMode === 'url' ? (
                                            <input
                                                type="url"
                                                value={urlContext}
                                                onChange={(e) => setUrlContext(e.target.value)}
                                                disabled={isGenerating}
                                                data-walkthrough="smart-transfer"
                                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 disabled:opacity-60"
                                                placeholder="https://example.com/recipe..."
                                                title="Paste a recipe URL from another site. We'll import it into your private cookbook for personal use. Please only import recipes you have permission to save and don't republish other creators' content as your own."
                                            />
                                        ) : (
                                            <textarea
                                                value={textContent}
                                                onChange={(e) => setTextContent(e.target.value)}
                                                disabled={isGenerating}
                                                data-walkthrough="smart-transfer"
                                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 disabled:opacity-60 resize-none font-mono text-xs overflow-y-auto"
                                                placeholder="Paste recipe content here (ingredients, instructions, etc.)..."
                                                style={{ minHeight: '80px' }}
                                                title="You can paste recipe text here for your own use. If this text came from another author or website, be sure you're allowed to use it and keep attribution accurate."
                                            />
                                        )}
                                    </div>

                                    {/* Recipe Type Toggle - Required before transfer */}
                                    <div className="relative flex-shrink-0 w-full sm:w-auto" data-walkthrough="multi-component-toggle">
                                        <div className="bg-neutral-50 p-3 hairline flex flex-col">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">RECIPE TYPE</div>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowRecipeTypeHelpTransfer(!showRecipeTypeHelpTransfer)}
                                                        onMouseEnter={() => setShowRecipeTypeHelpTransfer(true)}
                                                        onMouseLeave={() => setShowRecipeTypeHelpTransfer(false)}
                                                        className="w-4 h-4 flex items-center justify-center text-xs font-mono bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity"
                                                        aria-label="Learn about recipe types"
                                                    >
                                                        ?
                                                    </button>
                                                    {showRecipeTypeHelpTransfer && (
                                                        <div 
                                                            className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-neutral-0 hairline z-50"
                                                            onMouseEnter={() => setShowRecipeTypeHelpTransfer(true)}
                                                            onMouseLeave={() => setShowRecipeTypeHelpTransfer(false)}
                                                            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                                        >
                                                            <div className="space-y-2 text-xs">
                                                                <div>
                                                                    <div className="font-mono uppercase tracking-wider text-neutral-900 mb-1">SIMPLE</div>
                                                                    <div className="text-neutral-900 opacity-80">
                                                                        A recipe with a single list of ingredients and steps. All ingredients are combined together, and steps follow a linear sequence. Examples: pasta dishes, stir-fries, one-pot meals.
                                                                    </div>
                                                                </div>
                                                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>
                                                                <div>
                                                                    <div className="font-mono uppercase tracking-wider text-neutral-900 mb-1">MULTI-COMPONENT</div>
                                                                    <div className="text-neutral-900 opacity-80">
                                                                        A recipe with separate parts that are prepared independently and then combined. Each component has its own ingredients and steps. Examples: pies (crust + filling), layer cakes (cake layers + frosting), sandwiches (bread + fillings).
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {/* Arrow pointing down */}
                                                            <div 
                                                                className="absolute top-full right-4"
                                                                style={{
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '5px solid transparent',
                                                                    borderRight: '5px solid transparent',
                                                                    borderTop: '5px solid #0D0D0D'
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={toggleMultiComponent}
                                                disabled={isGenerating}
                                                className={`px-3 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                                                    formData.isMultiComponent
                                                        ? 'bg-neutral-900 text-neutral-0'
                                                        : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                {formData.isMultiComponent ? 'MULTI-COMPONENT' : 'SIMPLE'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Transfer Button and Checkbox Row */}
                                <div className="flex items-center gap-2">
                                    {/* Transfer Button */}
                                    <button
                                        type="button"
                                        onClick={handleAIGeneration}
                                        disabled={
                                            isGenerating || 
                                            (transferMode === 'url' ? !urlContext.trim() : !textContent.trim()) ||
                                            (hasSeenAcknowledgment && !acknowledgmentAccepted) ||
                                            !recipeTypeExplicitlySet
                                        }
                                        data-walkthrough="transfer-button"
                                        className="flex-1 px-4 py-2 h-10 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={!recipeTypeExplicitlySet ? "Please select a recipe type (Simple or Multi-component) before transferring" : ""}
                                    >
                                        {isGenerating ? (
                                            <>
                                                <span className="animate-spin inline-block mr-2">⚡</span>
                                                TRANSFERRING...
                                            </>
                                        ) : (
                                            '✨ TRANSFER'
                                        )}
                                    </button>

                                    {/* Simple Acknowledgment Checkbox (for users who have seen modal before) */}
                                    {hasSeenAcknowledgment && (transferMode === 'url' ? urlContext.trim() : textContent.trim()) && !isGenerating && (
                                        <div className="flex items-center gap-2 flex-shrink-0" data-walkthrough="acknowledgment-checkbox">
                                            <label className="checkbox-label flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={acknowledgmentAccepted}
                                                    onChange={(e) => setAcknowledgmentAccepted(e.target.checked)}
                                                    className="mt-0"
                                                />
                                                <span className="text-xs text-neutral-900 font-mono uppercase tracking-wider whitespace-nowrap">
                                                    I agree to the{' '}
                                                    <Link 
                                                        to="/terms-of-service" 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="underline hover:opacity-60"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        transfer terms
                                                    </Link>
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                        </div>
                        </div>
                    </div>
                </div>

                {/* Main Recipe Card - Unified Layout */}
                <div className="w-full flex justify-center px-0">
                    <div className="w-full max-w-[95%] sm:max-w-[630px] md:max-w-[1000px] relative group">
                        <div className="bg-neutral-0 hairline p-6 relative z-10 transition-all duration-200 overflow-y-auto min-h-[95vh] sm:min-h-[600px] sm:max-h-[90vh]">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                            {/* Left Section - Text Content */}
                            <div className="h-full relative overflow-y-auto no-scrollbar">
                                <div className="space-y-6">
                                    {/* Recipe Header - Notecard Style */}
                                    <div className="mb-4">
                                        {/* Recipe Title - Typewriter font, bottom left aligned */}
                                        <div className="text-left mb-3">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    data-walkthrough="recipe-name-field"
                                                    className="w-full text-4xl font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1"
                                                    style={{ color: colorTokens.oxfordBlue }}
                                                    placeholder="Enter recipe name"
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Red dividing line */}
                                        <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                        
                                        {/* Author info - centered */}
                                        <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                            <span>CREATING RECIPE</span>
                                            <span>•</span>
                                            <span>BY {currentUser?.displayName || 'YOU'}</span>
                                        </div>
                                    </div>

                                    {/* Recipe Information */}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">TIME</div>
                                                <input
                                                    type="number"
                                                    name="cooking_time"
                                                    value={formData.cooking_time}
                                                    onChange={handleChange}
                                                    min="1"
                                                    className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                    placeholder="45"
                                                />
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">CATEGORY</div>
                                                <select
                                                    name="category"
                                                    value={formData.category}
                                                    onChange={handleChange}
                                                    className="w-full text-center bg-transparent border-none outline-none focus:bg-neutral-50 px-2 py-1 font-mono text-lg"
                                                >
                                                    <option value="">Select category</option>
                                                    {categories.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">STATUS</div>
                                                <div className="font-mono text-lg">
                                                    CREATING
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recipe Type Selection */}
                                    <div className="bg-neutral-50 p-3 hairline">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900">RECIPE TYPE</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={toggleMultiComponent}
                                            className={`w-full px-3 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
                                                formData.isMultiComponent
                                                    ? 'bg-neutral-900 text-neutral-0'
                                                    : 'bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0'
                                            }`}
                                        >
                                            {formData.isMultiComponent ? 'MULTI-COMPONENT' : 'SIMPLE'}
                                        </button>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Conditional Rendering based on isMultiComponent */}
                                    {formData.isMultiComponent ? (
                                        <>
                                            {/* Multi-Component Mode */}
                                            <div>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900">COMPONENTS</div>
                                                    <button
                                                        type="button"
                                                        onClick={addComponent}
                                                        data-walkthrough="add-component-button"
                                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                                    >
                                                        + ADD COMPONENT
                                                    </button>
                                                </div>
                                                <Text className="mb-4 opacity-60 text-sm">
                                                    Each component has its own ingredients, prerequisites, and steps. All ingredients should be in their respective components.
                                                </Text>
                                                
                                                {formData.components.map((component, index) => (
                                                    <RecipeComponentEditor
                                                        key={component.id || index}
                                                        component={component}
                                                        onChange={(updated) => updateComponent(index, updated)}
                                                        onRemove={formData.components.length > 1 ? () => removeComponent(index) : null}
                                                        accentColor={index % 2 === 0 ? colorTokens.notecardBlue : colorTokens.oxfordBlue}
                                                    />
                                                ))}
                                            </div>

                                            {/* Section Separator */}
                                            <div className="border-t" style={{ borderColor: colorTokens.red }}></div>

                                            {/* Assembly Section */}
                                            <div data-walkthrough="assembly-section">
                                                <AssemblyEditor
                                                    assembly={formData.assembly}
                                                    onChange={updateAssembly}
                                                    componentNames={formData.components.map(c => c.name).filter(Boolean)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Simple Mode - Original UI */}
                                    {/* Ingredients */}
                                    <div data-walkthrough="ingredients-section">
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INGREDIENTS</div>
                                        <div className="space-y-3">
                                            {formData.ingredients.map((ingredient, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <textarea
                                                        value={ingredient}
                                                        onChange={(e) => handleListChange('ingredients', index, e.target.value)}
                                                        placeholder="Enter ingredient with quantity (e.g., 2 cups flour)"
                                                        rows={1}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '20px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                        onFocus={(e) => {
                                                            setTimeout(() => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = (e.target.scrollHeight + 2) + 'px';
                                                            }, 0);
                                                        }}
                                                    />
                                                    {formData.ingredients.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeListItem('ingredients', index)}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => addListItem('ingredients')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD INGREDIENT
                                        </button>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Prerequisites */}
                                    <div data-walkthrough="prerequisites-section">
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                                        <Text className="mb-3 opacity-60 text-sm">
                                            Preparation steps that need to be done before cooking begins.
                                        </Text>
                                        <div className="space-y-3">
                                            {formData.prerequisites.map((prerequisite, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <textarea
                                                        value={prerequisite}
                                                        onChange={(e) => handleListChange('prerequisites', index, e.target.value)}
                                                        placeholder="e.g., Preheat oven to 350°F"
                                                        rows={1}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '20px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                        onFocus={(e) => {
                                                            setTimeout(() => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = (e.target.scrollHeight + 2) + 'px';
                                                            }, 0);
                                                        }}
                                                    />
                                                    {formData.prerequisites.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeListItem('prerequisites', index)}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => addListItem('prerequisites')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD PREREQUISITE
                                        </button>
                                    </div>

                                    {/* Section Separator */}
                                    <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                    {/* Instructions */}
                                    <div data-walkthrough="steps-section">
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INSTRUCTIONS</div>
                                        <Text className="mb-3 opacity-60 text-sm">
                                            Step-by-step cooking instructions in order.
                                        </Text>
                                        <div className="space-y-3">
                                            {formData.steps.map((step, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}
                                                    </span>
                                                    <textarea
                                                        value={step}
                                                        onChange={(e) => handleListChange('steps', index, e.target.value)}
                                                        placeholder="Describe this cooking step in detail"
                                                        rows={1}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '20px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                        onFocus={(e) => {
                                                            setTimeout(() => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = (e.target.scrollHeight + 2) + 'px';
                                                            }, 0);
                                                        }}
                                                    />
                                                    {formData.steps.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeListItem('steps', index)}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                    {/* Active/Inactive Timer Display */}
                                                    <div className="flex items-center ml-2 h-8">
                                                        {(() => {
                                                            const segments = stepTimingSegments[index] || [];
                                                            const activeSegment = segments.find(s => s.kind === 'active');
                                                            const inactiveSegment = segments.find(s => s.kind === 'inactive');
                                                            const hasTimers = (activeSegment && inactiveSegment);
                                                            
                                                            if (hasTimers) {
                                                                return (
                                                                    <div className="flex items-center gap-1">
                                                                        {/* Always show active timer button, even if 0 */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                            className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                            style={{
                                                                                backgroundColor: '#fee2e2',
                                                                                border: '1px solid #fca5a5',
                                                                                color: '#991b1b'
                                                                            }}
                                                                            title={activeSegment.label || 'Active time'}
                                                                        >
                                                                            <TimerIcon size={14} className="mr-1" />
                                                                            {activeSegment.expectedSeconds > 0 ? `${Math.floor(activeSegment.expectedSeconds / 60)}m` : '0m'}
                                                                        </button>
                                                                        {/* Always show inactive timer button, even if 0 */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: segments })}
                                                                            className="flex items-center px-2 py-1 text-xs font-mono transition-colors h-8"
                                                                            style={{
                                                                                backgroundColor: '#dbeafe',
                                                                                border: '1px solid #93c5fd',
                                                                                color: '#1e40af'
                                                                            }}
                                                                            title={inactiveSegment.label || 'Inactive time'}
                                                                        >
                                                                            <TimerIcon size={14} className="mr-1" />
                                                                            {inactiveSegment.expectedSeconds > 0 ? `${Math.floor(inactiveSegment.expectedSeconds / 60)}m` : '0m'}
                                                                        </button>
                                                                    </div>
                                                                );
                                                            }
                                                            // Show add timer icon for steps without timers
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className="ml-2 flex items-center px-2 py-1 bg-neutral-50 text-neutral-600 hairline hover:bg-neutral-100 transition-colors h-8 text-xs font-mono"
                                                                    onClick={() => setTimerModal({ open: true, stepIndex: index, timingSegments: [] })}
                                                                    title="Add timers"
                                                                >
                                                                    <TimerIcon size={16} />
                                                                </button>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => addListItem('steps')}
                                            className="mt-2 px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            + ADD STEP
                                        </button>
                                    </div>
                                        </>
                                    )}
                                    {/* End of conditional rendering for simple/multi-component */}

                                </div>
                            </div>

                            {/* Right Section - Image Management */}
                            <div className="h-full overflow-hidden h-[40vh] sm:h-full overflow-y-auto">
                                <div className="h-full flex flex-col">
                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">RECIPE IMAGES</div>
                                    
                                    {/* Image Grid */}
                                    <div className="flex-1 overflow-y-auto">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {/* Image Placeholders/Previews */}
                                            {imagePreviews.map((preview, index) => (
                                                <div key={index} className="relative group aspect-square">
                                                    <div className="w-full h-full overflow-hidden hairline bg-neutral-50">
                                                        <img 
                                                            src={preview} 
                                                            alt={`Preview ${index + 1}`} 
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImage(index)}
                                                        className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Remove image"
                                                    >
                                                        ✕
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            // Replace image at this index
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = (e) => {
                                                                if (e.target.files && e.target.files.length > 0) {
                                                                    const file = e.target.files[0];
                                                                    // Validate file
                                                                    if (!file.type.startsWith('image/')) {
                                                                        setError('Please select a valid image file');
                                                                        return;
                                                                    }
                                                                    if (file.size > 10 * 1024 * 1024) {
                                                                        setError('File is too large (max 10MB)');
                                                                        return;
                                                                    }
                                                                    // Replace file at index
                                                                    setImageFiles(prev => {
                                                                        const newFiles = [...prev];
                                                                        newFiles[index] = file;
                                                                        return newFiles;
                                                                    });
                                                                    // Replace preview
                                                                    const reader = new FileReader();
                                                                    reader.onload = (e) => {
                                                                        setImagePreviews(prev => {
                                                                            const newPreviews = [...prev];
                                                                            newPreviews[index] = e.target.result;
                                                                            return newPreviews;
                                                                        });
                                                                    };
                                                                    reader.readAsDataURL(file);
                                                                }
                                                            };
                                                            input.click();
                                                        }}
                                                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                                                        title="Replace image"
                                                    >
                                                        <span className="text-white font-mono text-xs uppercase tracking-wider">REPLACE</span>
                                                    </button>
                                                </div>
                                            ))}
                                            
                                            {/* Add Image Placeholder - Clickable Square */}
                                            {imagePreviews.length < 10 && (
                                                <div 
                                                    className={`relative aspect-square hairline cursor-pointer transition-colors flex items-center justify-center group ${
                                                        dragOver ? 'bg-neutral-200' : 'bg-neutral-50 hover:bg-neutral-100'
                                                    }`}
                                                    onClick={triggerFileInput}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                >
                                                    <div className="text-center">
                                                        <div className="text-3xl mb-2 opacity-60 group-hover:opacity-80">+</div>
                                                        <div className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 group-hover:opacity-80">
                                                            ADD IMAGE
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Hidden File Input */}
                                        <input
                                            id="card-image-input"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileInput}
                                            className="hidden"
                                        />
                                        
                                        {imagePreviews.length > 0 && (
                                            <div className="mt-3 text-xs text-neutral-600 font-mono uppercase tracking-wider">
                                                Hover over images to edit or remove
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="max-w-5xl mx-auto mt-6">
                    <form onSubmit={handleSubmit} className="contents">
                        <div className="flex justify-center items-center space-x-4">
                            <button
                                type="submit"
                                data-walkthrough="create-recipe-button"
                                disabled={isSubmitting}
                                className={`px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider transition-opacity ${
                                    isSubmitting 
                                        ? 'opacity-60 cursor-not-allowed' 
                                        : 'hover:opacity-60'
                                }`}
                            >
                                {isSubmitting ? 'CREATING...' : 'CREATE RECIPE'}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/recipes')}
                                disabled={isSubmitting}
                                className={`px-6 py-3 bg-neutral-50 text-neutral-900 hairline transition-colors font-mono text-sm uppercase tracking-wider ${
                                    isSubmitting 
                                        ? 'opacity-60 cursor-not-allowed' 
                                        : 'hover:bg-neutral-900 hover:text-neutral-0'
                                }`}
                            >
                                CANCEL
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <TimerEditModal
                open={timerModal.open}
                onClose={() => setTimerModal({ open: false })}
                onSave={(newSegments) => {
                    setStepTimingSegments(prev => ({
                        ...prev,
                        [timerModal.stepIndex]: newSegments
                    }));
                    setTimerModal({ open: false });
                }}
                onRemove={() => {
                    setStepTimingSegments(prev => {
                        const updated = { ...prev };
                        delete updated[timerModal.stepIndex];
                        return updated;
                    });
                    setTimerModal({ open: false });
                }}
                stepIndex={timerModal.stepIndex}
                timingSegments={timerModal.timingSegments}
            />

            {/* Smart Transfer Info Modal */}
            {showSmartTransferInfo && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-neutral-0 hairline p-6 max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <Heading level={2} style={{ color: '#1b263b' }}>How Smart Transfer Works (and What's Allowed)</Heading>
                            <button
                                onClick={() => setShowSmartTransferInfo(false)}
                                className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0 modal-close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <Text size="sm" className="text-neutral-900 mb-4">
                                    Smart Transfer lets you quickly save recipes from other websites into your personal cookbook on 4RKED.
                                </Text>
                            </div>

                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium mb-2">What Smart Transfer does:</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc">
                                    <li>Pulls in the recipe title, ingredients, instructions, and (when available) the source URL and author name</li>
                                    <li>Stores this recipe in your private account by default for your personal convenience</li>
                                </ul>
                            </div>

                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium mb-2">Important legal basics:</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc">
                                    <li>The facts of a recipe (ingredients, measurements, basic steps) are not protected by copyright.</li>
                                    <li>The creative parts of a recipe (story, descriptions, photos, unique wording of instructions, and overall presentation) are protected by copyright and belong to the original creator.</li>
                                    <li>Using Smart Transfer does not give you ownership of someone else's recipe or the right to republish it as your own.</li>
                                </ul>
                            </div>

                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium mb-2">Personal use vs. public sharing:</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc">
                                    <li>You can use Smart Transfer to save recipes for your personal use – e.g., cooking at home, meal planning, grocery lists.</li>
                                    <li>You should not publicly share or publish a recipe you imported from another website unless you have permission or have written your own original version of the content.</li>
                                    <li>When you share an imported recipe in 4RKED, we may restrict what can be shown publicly (for example, only ingredients + link to the original recipe) to help protect the original author's rights.</li>
                                </ul>
                            </div>

                            <div>
                                <Text size="sm" className="text-neutral-900 font-medium mb-2">Attribution & credit:</Text>
                                <ul className="text-neutral-900 text-sm space-y-1 ml-4 list-disc">
                                    <li>Always keep the original source URL and author name accurate.</li>
                                    <li>Do not rename someone else's recipe as your own or remove their credit.</li>
                                </ul>
                            </div>

                            <div className="bg-neutral-50 p-4 hairline">
                                <Text size="sm" className="text-neutral-900">
                                    For full details, please review our{' '}
                                    <Link to="/terms-of-service" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                                    {' '}and{' '}
                                    <Link to="/dmca-policy" className="underline" target="_blank" rel="noopener noreferrer">DMCA Policy</Link>.
                                </Text>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowSmartTransferInfo(false)}
                                    className="px-4 py-2 bg-neutral-900 text-white hover:opacity-60 transition-colors font-mono text-sm uppercase tracking-wider"
                                >
                                    GOT IT
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Author Input Modal */}
            <Modal
                isOpen={showAuthorModal}
                onClose={() => setShowAuthorModal(false)}
                title="Recipe Author"
            >
                <div className="vspace">
                    <Text>
                        We couldn't automatically detect the original recipe author. Please enter the author's name to give proper credit.
                    </Text>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Author Name
                            </label>
                            <input
                                type="text"
                                value={manualAuthor}
                                onChange={(e) => setManualAuthor(e.target.value)}
                                placeholder="Enter the original recipe author's name"
                                className="w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                        </div>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={() => {
                                    if (manualAuthor.trim()) {
                                        setFormData(prev => ({ ...prev, author: manualAuthor.trim() }));
                                    }
                                    setShowAuthorModal(false);
                                }}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                SAVE AUTHOR
                            </button>
                            <button
                                onClick={() => setShowAuthorModal(false)}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                SKIP
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Paste Attribution Modal - shown before transfer starts */}
            <Modal
                isOpen={showPasteAttributionModal}
                onClose={() => setShowPasteAttributionModal(false)}
                title="Recipe Attribution"
            >
                <div className="vspace">
                    <Text className="mb-4">
                        To properly credit this recipe as a transferred recipe, please provide the following information:
                    </Text>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Original Recipe Author *
                            </label>
                            <input
                                type="text"
                                value={pasteAuthor}
                                onChange={(e) => setPasteAuthor(e.target.value)}
                                placeholder="e.g., Gordon Ramsay, Bon Appétit, etc."
                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                            <Text size="xs" className="text-neutral-900 opacity-60 mt-1">
                                Who created this recipe?
                            </Text>
                        </div>

                        <div>
                            <label className="block text-sm font-mono uppercase tracking-wider text-neutral-900 mb-2">
                                Source URL (Optional)
                            </label>
                            <input
                                type="url"
                                value={pasteSourceUrl}
                                onChange={(e) => setPasteSourceUrl(e.target.value)}
                                placeholder="https://example.com/recipe"
                                className="w-full px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                            />
                            <Text size="xs" className="text-neutral-900 opacity-60 mt-1">
                                Where did you find this recipe? (helps give proper credit)
                            </Text>
                        </div>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={async () => {
                                    if (!pasteAuthor.trim()) {
                                        setError('Please enter the recipe author name to continue.');
                                        return;
                                    }
                                    setShowPasteAttributionModal(false);
                                    await performTransfer();
                                }}
                                disabled={!pasteAuthor.trim()}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ✨ START TRANSFER
                            </button>
                            <button
                                onClick={() => {
                                    setShowPasteAttributionModal(false);
                                    setPasteAuthor('');
                                    setPasteSourceUrl('');
                                }}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Access Denied Modal */}
            <Modal
                isOpen={showAccessDeniedModal}
                onClose={() => setShowAccessDeniedModal(false)}
                title="Website Blocking Access"
            >
                <div className="vspace">
                    <div className="bg-yellow-50 p-4 hairline border-l-4 border-l-yellow-400 mb-4">
                        <Text className="text-yellow-900 font-mono text-xs uppercase tracking-wider mb-2">
                            ⚠️ WEBSITE DOES NOT ALLOW SCRAPING
                        </Text>
                        <Text size="sm" className="text-yellow-900">
                            Oops! This website is blocking automated requests and won't let us extract the recipe directly.
                        </Text>
                    </div>
                    
                    <div className="bg-green-50 p-4 hairline border-l-4 border-l-green-400 mb-4">
                        <Text className="text-green-900 font-mono text-xs uppercase tracking-wider mb-2">
                            ✨ BUT YOU CAN STILL COPY & PASTE!
                        </Text>
                        <Text size="sm" className="text-green-900 mb-3">
                            Simply copy the recipe content from the website and paste it directly here. Our AI will extract the ingredients and instructions for you.
                        </Text>
                        <ol className="text-green-900 text-xs space-y-2 ml-4 list-decimal">
                            <li>Open the recipe website in your browser</li>
                            <li>Select and copy all the recipe content (ingredients, instructions, etc.)</li>
                            <li>Return here and click the <strong>"📋 PASTE TEXT"</strong> tab above</li>
                            <li>Paste the content and click <strong>"✨ TRANSFER"</strong></li>
                        </ol>
                    </div>
                    
                    <div className="flex space-x-4 pt-4">
                        <button
                            onClick={() => {
                                setShowAccessDeniedModal(false);
                                setTransferMode('text');
                            }}
                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                        >
                            📋 SWITCH TO PASTE MODE
                        </button>
                        <button
                            onClick={() => setShowAccessDeniedModal(false)}
                            className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                        >
                            CLOSE
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Upgrade Prompt Modal */}
            <UpgradePrompt 
                isOpen={showUpgradePrompt}
                onClose={() => setShowUpgradePrompt(false)}
                currentPlan={quotaCheck?.planType || 'free'}
            />

            {/* First-Time Acknowledgment Modal */}
            <Modal
                isOpen={showAcknowledgmentModal}
                onClose={() => setShowAcknowledgmentModal(false)}
                maxWidth="max-w-modal-md"
            >
                <div className="space-y-4">
                    <Heading level={2} style={{ color: '#1b263b' }}>Smart Transfer Terms</Heading>
                    <div className="space-y-3">
                        <Text className="text-neutral-900">
                            Before using Smart Transfer, please read and confirm the following:
                        </Text>
                        <div className="bg-neutral-50 p-4 hairline space-y-3">
                            <Text size="sm" className="text-neutral-900">
                                <strong>Personal Use Only:</strong> Recipes transferred using Smart Transfer are saved to your private cookbook for your personal use only.
                            </Text>
                            <Text size="sm" className="text-neutral-900">
                                <strong>Attribution:</strong> You must keep the original author and source URL accurate. Do not remove or falsify attribution information.
                            </Text>
                            <Text size="sm" className="text-neutral-900">
                                <strong>No Republishing:</strong> Do not publish imported recipes as your own original work without permission from the original author.
                            </Text>
                            <Text size="sm" className="text-neutral-900">
                                <strong>Sharing Restrictions:</strong> Public sharing of imported recipes may be restricted to protect the original creator's rights.
                            </Text>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={handleAcknowledgmentAccept}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                I AGREE
                            </button>
                            <button
                                onClick={() => setShowAcknowledgmentModal(false)}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CreateRecipe;
